import { NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Injector,
  OnDestroy,
  TemplateRef,
  afterNextRender,
  computed,
  contentChild,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';

@Component({
  selector: 'ces-card-carousel',
  standalone: true,
  imports: [NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ces-card-carousel.component.html',
  styleUrl: './ces-card-carousel.component.scss',
})
export class CesCardCarouselComponent<T> implements AfterViewInit, OnDestroy {
  readonly value = input.required<readonly T[]>();
  readonly showNavigators = input(true);
  readonly showIndicators = input(true);

  protected readonly itemTemplate = contentChild.required<TemplateRef<{ $implicit: T }>>('item');
  protected readonly viewportRef = viewChild.required<ElementRef<HTMLElement>>('viewport');

  protected readonly scrollLeft = signal(0);
  protected readonly viewportWidth = signal(0);
  protected readonly scrollWidth = signal(0);
  protected readonly cardStep = signal(0);

  protected readonly hasOverflow = computed(() => this.scrollWidth() > this.viewportWidth() + 1);
  protected readonly canScrollPrev = computed(() => this.hasOverflow() && this.scrollLeft() > 1);
  protected readonly canScrollNext = computed(
    () => this.hasOverflow() && this.scrollLeft() + this.viewportWidth() < this.scrollWidth() - 1,
  );

  private readonly maxScrollLeft = computed(() => Math.max(0, this.scrollWidth() - this.viewportWidth()));

  protected readonly pageCount = computed(() => {
    const step = this.cardStep();
    if (!this.hasOverflow() || step <= 0) return 0;
    // Au moins 2 pages dès qu'il y a débordement : sans ce plancher, un débordement
    // inférieur à une largeur de card donne une page unique qui est aussi la dernière,
    // et scrollToPage(0) recible la fin de course au lieu du début (flèche gauche inerte).
    return Math.max(2, Math.floor(this.maxScrollLeft() / step) + 1);
  });

  protected readonly activePage = computed(() => {
    const step = this.cardStep();
    const lastIndex = Math.max(0, this.pageCount() - 1);
    if (step <= 0) return 0;
    if (this.scrollLeft() >= this.maxScrollLeft() - 1) return lastIndex;
    return Math.min(Math.round(this.scrollLeft() / step), lastIndex);
  });

  protected readonly pages = computed(() => Array.from({ length: this.pageCount() }, (_, i) => i));

  private resizeObserver?: ResizeObserver;
  private scrollHandler = (): void => {
    const vp = this.viewportRef().nativeElement;
    this.scrollLeft.set(vp.scrollLeft);
  };

  constructor() {
    effect(() => {
      this.value();
      afterNextRender(() => this.measure(), { injector: this.injector });
    });
  }

  private readonly injector = inject(Injector);

  ngAfterViewInit(): void {
    const vp = this.viewportRef().nativeElement;
    this.resizeObserver = new ResizeObserver(() => this.measure());
    this.resizeObserver.observe(vp);
    vp.addEventListener('scroll', this.scrollHandler, { passive: true });
    this.measure();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  private measure(): void {
    const vp = this.viewportRef().nativeElement;
    this.viewportWidth.set(vp.clientWidth);
    this.scrollWidth.set(vp.scrollWidth);
    const first = vp.firstElementChild as HTMLElement | null;
    if (first) {
      const cs = getComputedStyle(vp);
      const gap = parseFloat(cs.columnGap || cs.gap || '0') || 0;
      this.cardStep.set(first.getBoundingClientRect().width + gap);
    }
  }

  protected scrollByOne(direction: -1 | 1): void {
    const lastIndex = Math.max(0, this.pageCount() - 1);
    this.scrollToPage(Math.min(lastIndex, Math.max(0, this.activePage() + direction)));
  }

  protected scrollToPage(index: number): void {
    const isLast = index === this.pageCount() - 1;
    const target = isLast ? this.maxScrollLeft() : Math.min(index * this.cardStep(), this.maxScrollLeft());
    this.viewportRef().nativeElement.scrollTo({ left: target, behavior: 'smooth' });
  }
}
